import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage, getLocale } from '../../contexts/LanguageContext';
import { useNotification } from '../../contexts/NotificationContext';
import PageHeader from '../ui/PageHeader';
import Icon from '../ui/Icon';
import api from '../../api';
import './ChannelConfigPage.css';

const ATTACK_TYPES = ['0', '1', '2', '3', '4', '5'];
const DEFENSE_TYPES = ['1', '2', '3', '4', '5'];

const enemiesLabel = (t, type) => (type === '1'
    ? t('channelPage.vsEnemy', 'Contre {n} ennemi', { n: type })
    : t('channelPage.vsEnemies', 'Contre {n} ennemis', { n: type }));

// Liste déroulante des salons texte ; bordure verte quand un salon est choisi
const ChannelSelect = ({ label, value, onChange, salons, hint }) => {
    const { t } = useLanguage();
    return (
        <label className="c2-field">
            <span className="c2-field__label">{label}</span>
            <select
                className={`c2-select${value ? ' channel-select--set' : ''}`}
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
            >
                <option value="">{t('channelPage.selectChannel', 'Sélectionner un salon')}</option>
                {salons.map(salon => (
                    <option key={salon.id} value={salon.id}># {salon.name}</option>
                ))}
            </select>
            {hint && <span className="c2-field__hint">{hint}</span>}
        </label>
    );
};

const ChannelConfigPage = () => {
    const { serverId } = useParams();
    const { t } = useLanguage();
    const { success, error: showError, warning } = useNotification();
    const formRef = useRef(null);

    const [configs, setConfigs] = useState([]);
    const [salons, setSalons] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Formulaire d'édition
    const [editMode, setEditMode] = useState(null); // null | 'create' | configId
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        specialRole: '',
        salons: {},
        isDefault: false
    });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            // Récupérer les canaux Discord
            const channelsRes = await api.get(`/discord/channels/${serverId}`).catch(async () => {
                const serverRes = await api.get(`/servers/${serverId}`);
                return { data: serverRes.data.salons || [] };
            });

            const textChannels = channelsRes.data
                .filter(channel => channel.type === 0)
                .sort((a, b) => a.name.localeCompare(b.name));
            setSalons(textChannels);

            // Récupérer les rôles Discord
            const rolesRes = await api.get(`/discord/roles/${serverId}`).catch(() => ({ data: [] }));
            setRoles(rolesRes.data || []);

            // Récupérer les configurations channel
            const configsRes = await api.get(`/channel-configs/${serverId}`);
            setConfigs(configsRes.data || []);

            // Si aucune configuration, proposer la migration
            if ((!configsRes.data || configsRes.data.length === 0)) {
                try {
                    const migrateRes = await api.post(`/channel-config/migrate/${serverId}`);
                    if (migrateRes.data.config && !migrateRes.data.alreadyMigrated) {
                        success(t('channelPage.migrated', 'Configuration existante migrée automatiquement !'));
                        setConfigs([migrateRes.data.config]);
                    } else if (migrateRes.data.alreadyMigrated) {
                        setConfigs([migrateRes.data.config]);
                    }
                } catch (migrateErr) {
                    // Pas de config existante à migrer, c'est normal
                }
            }
        } catch (err) {
            console.error('Erreur lors du chargement:', err);
            showError(t('channelPage.loadError', 'Erreur lors du chargement des données'));
        } finally {
            setLoading(false);
        }
    }, [serverId, success, showError, t]);

    useEffect(() => {
        if (serverId) fetchData();
    }, [serverId, fetchData]);

    // Amener le formulaire à l'écran quand on l'ouvre
    useEffect(() => {
        if (editMode) formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [editMode]);

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            specialRole: '',
            salons: {},
            isDefault: false
        });
        setEditMode(null);
    };

    const openCreateForm = () => {
        resetForm();
        setFormData(prev => ({
            ...prev,
            isDefault: configs.length === 0
        }));
        setEditMode('create');
    };

    const openEditForm = (config) => {
        // Extraire les channelIds des salons
        const salonsObj = {};
        if (config.salons) {
            const salonsData = config.salons instanceof Map ? Object.fromEntries(config.salons) : config.salons;
            Object.keys(salonsData).forEach(key => {
                const salon = salonsData[key];
                if (salon && typeof salon === 'object' && salon.channelId) {
                    salonsObj[key] = salon.channelId;
                } else if (typeof salon === 'string') {
                    salonsObj[key] = salon;
                }
            });
        }

        setFormData({
            name: config.name || '',
            description: config.description || '',
            specialRole: config.specialRoleId || '',
            salons: salonsObj,
            isDefault: config.isDefault || false
        });
        setEditMode(config._id);
    };

    const handleSalonChange = (key, value) => {
        setFormData(prev => ({
            ...prev,
            salons: { ...prev.salons, [key]: value }
        }));
    };

    const handleSave = async () => {
        if (!formData.name.trim()) {
            warning(t('channelPage.nameRequired', 'Le nom de la configuration est requis'));
            return;
        }

        setSaving(true);
        try {
            const payload = {
                serverId,
                name: formData.name.trim(),
                description: formData.description,
                salons: formData.salons,
                specialRole: formData.specialRole,
                isDefault: formData.isDefault
            };

            if (editMode === 'create') {
                await api.post('/channel-config', payload);
                success(t('channelPage.created', 'Configuration créée avec succès !'));
            } else {
                await api.put(`/channel-config/${editMode}`, payload);
                success(t('channelPage.updated', 'Configuration mise à jour avec succès !'));
            }

            resetForm();
            await fetchData();
        } catch (err) {
            console.error('Erreur sauvegarde:', err);
            if (err.response?.status === 409) {
                showError(t('channelPage.nameTaken', 'Une configuration avec ce nom existe déjà'));
            } else {
                showError(t('channelPage.saveError', 'Erreur lors de la sauvegarde'));
            }
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (configId, configName) => {
        if (!window.confirm(t('channelPage.confirmDelete', 'Supprimer la configuration « {name} » ?', { name: configName }))) return;

        try {
            await api.delete(`/channel-config/${configId}`);
            success(t('channelPage.deleted', 'Configuration « {name} » supprimée', { name: configName }));
            if (editMode === configId) resetForm();
            await fetchData();
        } catch (err) {
            console.error('Erreur suppression:', err);
            showError(t('channelPage.deleteError', 'Erreur lors de la suppression'));
        }
    };

    const handleSetDefault = async (configId) => {
        try {
            await api.post(`/channel-config/${configId}/set-default`);
            success(t('channelPage.defaultSet', 'Configuration définie comme défaut'));
            await fetchData();
        } catch (err) {
            console.error('Erreur set-default:', err);
            showError(t('channelPage.updateError', 'Erreur lors de la mise à jour'));
        }
    };

    const handleDuplicate = (config) => {
        const salonsObj = {};
        if (config.salons) {
            const salonsData = config.salons instanceof Map ? Object.fromEntries(config.salons) : config.salons;
            Object.keys(salonsData).forEach(key => {
                const salon = salonsData[key];
                if (salon && typeof salon === 'object' && salon.channelId) {
                    salonsObj[key] = salon.channelId;
                }
            });
        }

        setFormData({
            name: `${config.name} (copie)`,
            description: config.description || '',
            specialRole: config.specialRoleId || '',
            salons: salonsObj,
            isDefault: false
        });
        setEditMode('create');
    };

    const getConfiguredChannelsCount = (config) => {
        if (!config.salons) return 0;
        const salonsData = config.salons instanceof Map ? Object.fromEntries(config.salons) : config.salons;
        return Object.values(salonsData).filter(s => s && (s.channelId || typeof s === 'string')).length;
    };

    if (loading) {
        return (
            <div className="c2-scope c2-admin-page">
                <div className="c2-loading">
                    <div className="c2-spinner" />
                    <p>{t('channelPage.loading', 'Chargement des configurations...')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="c2-scope c2-admin-page">
            <PageHeader
                icon="sliders"
                title={t('channelConfig.title', 'Configurations de Channels')}
                subtitle={t('channelConfig.subtitle', 'Gérez plusieurs configurations de salons pour différentes sections du serveur')}
                actions={
                    <button type="button" className="c2-btn c2-btn--primary" onClick={openCreateForm}>
                        <Icon name="plus" size={18} />
                        {t('channelPage.newConfig', 'Nouvelle configuration')}
                    </button>
                }
            />

            <div className="channel-meta">
                <span className="c2-chip">{configs.length > 1 ? t('channelPage.configsN', '{n} configurations', { n: configs.length }) : t('channelPage.config1', '{n} configuration', { n: configs.length })}</span>
                <span className="c2-chip"><Icon name="hash" size={14} />{salons.length > 1 ? t('channelPage.channelsAvailable', '{n} salons disponibles', { n: salons.length }) : t('channelPage.channelAvailable', '{n} salon disponible', { n: salons.length })}</span>
            </div>

            {/* Liste des configurations existantes */}
            {configs.length === 0 ? (
                <section className="c2-card c2-empty">
                    <Icon name="sliders" size={36} />
                    <p>{t('channelPage.none', 'Aucune configuration créée')}</p>
                    <p className="c2-field__hint">{t('channelPage.noneHint', 'Créez votre première configuration pour commencer')}</p>
                </section>
            ) : (
                <div className="channel-grid">
                    {configs.map(config => {
                        const count = getConfiguredChannelsCount(config);
                        return (
                            <article
                                key={config._id}
                                className={`c2-card channel-card${editMode === config._id ? ' is-editing' : ''}${config.isDefault ? ' is-default' : ''}`}
                            >
                                <div className="channel-card__head">
                                    <div className="channel-card__title">
                                        <h2>{config.name}</h2>
                                        {config.isDefault && (
                                            <span className="c2-chip c2-chip--purple"><Icon name="star" size={13} filled />{t('channelPage.default', 'Défaut')}</span>
                                        )}
                                    </div>
                                    <div className="channel-card__actions">
                                        {!config.isDefault && (
                                            <button type="button" className="c2-icon-btn c2-icon-btn--sm" onClick={() => handleSetDefault(config._id)} title={t('channelPage.setDefault', 'Définir comme défaut')} aria-label={t('channelPage.setDefault', 'Définir comme défaut')}>
                                                <Icon name="star" size={16} />
                                            </button>
                                        )}
                                        <button type="button" className="c2-icon-btn c2-icon-btn--sm" onClick={() => handleDuplicate(config)} title={t('channelPage.duplicate', 'Dupliquer')} aria-label={t('channelPage.duplicate', 'Dupliquer')}>
                                            <Icon name="copy" size={16} />
                                        </button>
                                        <button type="button" className="c2-icon-btn c2-icon-btn--sm" onClick={() => openEditForm(config)} title={t('common.edit', 'Modifier')} aria-label={t('common.edit', 'Modifier')}>
                                            <Icon name="pencil" size={16} />
                                        </button>
                                        <button type="button" className="c2-icon-btn c2-icon-btn--sm c2-icon-btn--danger" onClick={() => handleDelete(config._id, config.name)} title={t('common.delete', 'Supprimer')} aria-label={t('common.delete', 'Supprimer')}>
                                            <Icon name="trash" size={16} />
                                        </button>
                                    </div>
                                </div>
                                {config.description && <p className="c2-muted channel-card__desc">{config.description}</p>}
                                <div className="channel-card__footer">
                                    <span className="c2-badge c2-badge--green">
                                        <span className="c2-diamond" />
                                        {count > 1 ? t('channelPage.configuredN', '{n} salons configurés', { n: count }) : t('channelPage.configured1', '{n} salon configuré', { n: count })}
                                    </span>
                                    <span className="c2-field__hint">{t('channelPage.modifiedOn', 'Modifiée le {date}', { date: new Date(config.updatedAt).toLocaleDateString(getLocale()) })}</span>
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}

            {/* Formulaire d'édition/création */}
            {editMode && (
                <section ref={formRef} className="c2-card c2-section channel-form">
                    <div className="c2-section__head">
                        <h2 className="c2-section__title">
                            <Icon name={editMode === 'create' ? 'plus' : 'pencil'} size={20} />
                            {editMode === 'create' ? t('channelPage.newConfig', 'Nouvelle configuration') : t('channelPage.editConfig', 'Modifier la configuration')}
                        </h2>
                        <button type="button" className="c2-icon-btn" onClick={resetForm} aria-label={t('channelPage.closeForm', 'Fermer le formulaire')}>
                            <Icon name="close" size={20} />
                        </button>
                    </div>

                    {/* Informations générales */}
                    <div className="channel-form__grid">
                        <label className="c2-field">
                            <span className="c2-field__label">{t('channelPage.name', 'Nom de la configuration *')}</span>
                            <input
                                type="text"
                                className="c2-input"
                                value={formData.name}
                                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                placeholder={t('channelPage.namePlaceholder', 'Ex: Section PvP, Section GvG...')}
                            />
                        </label>
                        <label className="c2-field">
                            <span className="c2-field__label">{t('channelPage.description', 'Description')}</span>
                            <input
                                type="text"
                                className="c2-input"
                                value={formData.description}
                                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                placeholder={t('channelPage.descriptionPlaceholder', 'Description optionnelle...')}
                            />
                        </label>
                        <label className="c2-field">
                            <span className="c2-field__label">{t('channelPage.specialRole', 'Rôle spécial')}</span>
                            <select
                                className="c2-select"
                                value={formData.specialRole}
                                onChange={(e) => setFormData(prev => ({ ...prev, specialRole: e.target.value }))}
                            >
                                <option value="">{t('channelPage.noSpecialRole', 'Aucun rôle spécial')}</option>
                                {roles.map(role => (
                                    <option key={role.id} value={role.id}>{role.name}</option>
                                ))}
                            </select>
                        </label>
                        <label className="channel-form__check">
                            <input
                                type="checkbox"
                                checked={formData.isDefault}
                                onChange={(e) => setFormData(prev => ({ ...prev, isDefault: e.target.checked }))}
                            />
                            <span>{t('channelPage.isDefault', 'Configuration par défaut')}</span>
                        </label>
                    </div>

                    {/* Salons Attaque & Défense */}
                    <div className="channel-form__battle">
                        <div className="c2-tile channel-block">
                            <div className="channel-block__head">
                                <span className="channel-block__icon"><Icon name="swords" size={18} /></span>
                                <h3>{t('channelPage.attack', 'Attaque')}</h3>
                                <span className="c2-chip">{t('channelPage.typesN', '{n} types', { n: ATTACK_TYPES.length })}</span>
                            </div>
                            {ATTACK_TYPES.map(type => (
                                <ChannelSelect
                                    key={`attaque-${type}`}
                                    label={enemiesLabel(t, type)}
                                    value={formData.salons[`attaqueVs${type}`]}
                                    onChange={(value) => handleSalonChange(`attaqueVs${type}`, value)}
                                    salons={salons}
                                />
                            ))}
                        </div>

                        <div className="c2-tile channel-block">
                            <div className="channel-block__head">
                                <span className="channel-block__icon channel-block__icon--purple"><Icon name="shield" size={18} /></span>
                                <h3>{t('channelPage.defense', 'Défense')}</h3>
                                <span className="c2-chip">{t('channelPage.typesN', '{n} types', { n: DEFENSE_TYPES.length })}</span>
                            </div>
                            {DEFENSE_TYPES.map(type => (
                                <ChannelSelect
                                    key={`defense-${type}`}
                                    label={enemiesLabel(t, type)}
                                    value={formData.salons[`defenseVs${type}`]}
                                    onChange={(value) => handleSalonChange(`defenseVs${type}`, value)}
                                    salons={salons}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Salons génériques : pour les serveurs avec peu de salons */}
                    <div className="channel-generic">
                        <div>
                            <h3>{t('channelPage.generic', 'Salons génériques')} <span className="c2-muted">{t('baremePage.optional', '(optionnel)')}</span></h3>
                            <p className="c2-section__desc">
                                {t('channelPage.genericDesc', 'Si vous n\'avez que 1 ou 2 salons pour toutes les attaques et défenses, utilisez ces champs comme solution de repli.')}
                            </p>
                        </div>
                        <div className="channel-form__battle">
                            <ChannelSelect
                                label={t('channelPage.anyAttack', 'Attaque — pour toutes les attaques')}
                                value={formData.salons.attaqueAny}
                                onChange={(value) => handleSalonChange('attaqueAny', value)}
                                salons={salons}
                                hint={t('channelPage.anyAttackHint', 'Utilisé si aucun salon spécifique (Attaque contre N) n\'est configuré')}
                            />
                            <ChannelSelect
                                label={t('channelPage.anyDefense', 'Défense — pour toutes les défenses')}
                                value={formData.salons.defenseAny}
                                onChange={(value) => handleSalonChange('defenseAny', value)}
                                salons={salons}
                                hint={t('channelPage.anyDefenseHint', 'Utilisé si aucun salon spécifique (Défense contre N) n\'est configuré')}
                            />
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="channel-form__actions">
                        <button type="button" className="c2-btn c2-btn--ghost" onClick={resetForm}>
                            {t('common.cancel', 'Annuler')}
                        </button>
                        <button type="button" className="c2-btn c2-btn--primary" onClick={handleSave} disabled={saving}>
                            <Icon name="save" size={18} />
                            {saving ? t('pepitesAdmin.saving', 'Sauvegarde...') : t('common.save', 'Sauvegarder')}
                        </button>
                    </div>
                </section>
            )}
        </div>
    );
};

export default ChannelConfigPage;
